import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Collapse } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { OptionsMenuProps, MenuItemData } from './types';

const buttonPaddingMap: Record<NonNullable<OptionsMenuProps['size']>, string> = {
  small: '4px',
  medium: '6px',
  large: '8px',
};

const getButtonPadding = (size: OptionsMenuProps['size'] = 'small') => buttonPaddingMap[size] ?? buttonPaddingMap.small;

const OptionsMenu = <T extends MenuItemData = MenuItemData>({
  options,
  data,
  ariaLabel = 'More actions',
  onAction,
  size = 'small',
  iconSize = '16px',
  menuItemHeight = '32px',
  fontSize = '12px',
  sx,
}: OptionsMenuProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  if (!options?.length) {
    return null;
  }

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleMenuItemClick = (option: T) => {
    if (option.expandable) {
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(option.key)) {
        newExpanded.delete(option.key);
      } else {
        newExpanded.add(option.key);
      }
      setExpandedItems(newExpanded);
      return;
    }

    option.onClick?.(data);
    onAction?.(option.key, data);
    handleClose();
  };

  const buttonPadding = getButtonPadding(size);

  return (
    <>
      <IconButton aria-label={ariaLabel} size={size} onClick={handleClick} sx={{ padding: buttonPadding, ...sx }}>
        <MoreVertIcon sx={{ fontSize: iconSize }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map(option => (
          <React.Fragment key={option.key}>
            <MenuItem
              onClick={() => handleMenuItemClick(option as unknown as T)}
              disabled={option.disabled && !option.expandable}
              sx={{ fontSize, minHeight: menuItemHeight }}
            >
              {option.icon && <ListItemIcon sx={{ minWidth: '24px' }}>{option.icon}</ListItemIcon>}
              <ListItemText primary={option.label} />
              {option.expandable && (expandedItems.has(option.key) ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </MenuItem>
            {option.expandable && option.subOptions && (
              <Collapse in={expandedItems.has(option.key)} timeout="auto" unmountOnExit>
                {option.subOptions.map(subOption => (
                  <MenuItem
                    key={subOption.key}
                    onClick={() => {
                      subOption.onClick?.(data);
                      onAction?.(subOption.key, data);
                      handleClose();
                    }}
                    disabled={subOption.disabled}
                    sx={{
                      fontSize,
                      minHeight: menuItemHeight,
                      paddingLeft: '48px',
                    }}
                  >
                    {subOption.icon && <ListItemIcon sx={{ minWidth: '24px' }}>{subOption.icon}</ListItemIcon>}
                    <ListItemText primary={subOption.label} />
                  </MenuItem>
                ))}
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
};

export default OptionsMenu;
